/* ============================================================
   Knowledge.js
   Central data store for the ASSOCHAM Knowledge Hub.
   Exports `reports`, `voices` and `bulletins` consumed by the
   listing pages (ASSOCHAMReport / ASSOCHAMVoice / ASSOCHAMBulletins)
   and the shared detail view.
   ============================================================ */

export const reports = [
  {
    id: "report-1",
    title: "India Manufacturing Outlook 2025",
    subtitle: "Sector Competitiveness & Growth Roadmap",
    category: "Industry Research",
    date: "March 2025",
    pages: 84,
    size: "4.2 MB",
    format: "PDF",
    author: "ASSOCHAM Research Bureau",
    accent: "from-brand via-crimson to-deeprose",
    icon: "factory",
    overview: [
      "The India Manufacturing Outlook 2025 presents a comprehensive assessment of the country's industrial landscape, evaluating the momentum gained through the Production-Linked Incentive (PLI) schemes, the National Manufacturing Policy and the evolving global supply-chain realignment.",
      "The report benchmarks India's manufacturing competitiveness against 12 peer economies, analysing cost of production, logistics efficiency, regulatory friction and skill availability. It projects that manufacturing could contribute 25% of GDP by 2030 if the recommended reform pipeline is implemented.",
      "Sector deep-dives cover automobiles, electronics, textiles, chemicals and capital goods, with dedicated chapters on MSME integration, quality infrastructure and the transition to Industry 4.0.",
    ],
    highlights: [
      "Manufacturing GDP share target: 25% by 2030",
      "12-economy competitiveness benchmark",
      "5 sector deep-dives with reform scorecards",
    ],
  },
  {
    id: "report-2",
    title: "MSME Competitiveness Index",
    subtitle: "Unlocking the Backbone of the Economy",
    category: "MSME & Entrepreneurship",
    date: "February 2025",
    pages: 62,
    size: "3.1 MB",
    format: "PDF",
    author: "ASSOCHAM MSME Council",
    accent: "from-ember via-sun to-brand",
    icon: "chart",
    overview: [
      "The MSME Competitiveness Index ranks 30 Indian states on the health of their micro, small and medium enterprise ecosystems, measuring access to finance, market linkages, technology adoption, infrastructure and regulatory ease.",
      "The study reveals that while coastal and southern states lead on overall competitiveness, several inland states are narrowing the gap through dedicated MSME facilitation councils and digital onboarding drives.",
      "The report proposes a five-pillar reform framework aimed at formalising 1 crore additional enterprises and doubling MSME exports within the next five years.",
    ],
    highlights: [
      "30-state competitiveness ranking",
      "Five-pillar formalisation & export roadmap",
      "1 crore enterprise formalisation target",
    ],
  },
  {
    id: "report-3",
    title: "Green Energy Transition Report",
    subtitle: "Pathways to Net-Zero Industry",
    category: "Sustainability & Energy",
    date: "January 2025",
    pages: 96,
    size: "5.4 MB",
    format: "PDF",
    author: "ASSOCHAM Climate Council",
    accent: "from-deeprose via-brand to-ember",
    icon: "leaf",
    overview: [
      "This report maps India's industrial pathway to net-zero emissions by 2070, quantifying the investment required across power, steel, cement, chemicals and transport, and identifying the technologies that will define the transition.",
      "It assesses the maturity of green hydrogen, carbon capture, renewable storage and circular-economy practices, and recommends a blended finance architecture to mobilise the estimated $10 trillion needed over the next four decades.",
      "A dedicated chapter addresses the social dimension of the transition, including skilling, reskilling and just-transition frameworks for fossil-fuel dependent regions.",
    ],
    highlights: [
      "$10 trillion blended-finance architecture",
      "Green hydrogen & CCUS technology readiness index",
      "Just-transition framework for 8 states",
    ],
  },
  {
    id: "report-4",
    title: "Digital Economy Readiness Study",
    subtitle: "India's Stack, Scale & Strategy",
    category: "Digital & Technology",
    date: "December 2024",
    pages: 71,
    size: "3.8 MB",
    format: "PDF",
    author: "ASSOCHAM Digital Council",
    accent: "from-sun via-ember to-crimson",
    icon: "chip",
    overview: [
      "The Digital Economy Readiness Study evaluates India's position in the global digital race, examining the strength of its public digital infrastructure, the maturity of its startup ecosystem and the depth of its talent pool.",
      "The report benchmarks India against 20 digital economies and identifies three strategic levers: AI and data governance, semiconductor self-reliance, and the digitisation of MSME supply chains.",
      "It concludes with a 10-point action plan to position India as a $1 trillion digital economy by 2030.",
    ],
    highlights: [
      "$1 trillion digital economy target by 2030",
      "20-economy benchmark comparison",
      "10-point AI & semiconductor action plan",
    ],
  },
  {
    id: "report-5",
    title: "Agriculture & Food Processing Report",
    subtitle: "From Farm to Global Markets",
    category: "Agri & Food",
    date: "November 2024",
    pages: 58,
    size: "2.9 MB",
    format: "PDF",
    author: "ASSOCHAM Agri Council",
    accent: "from-ember via-brand to-deeprose",
    icon: "wheat",
    overview: [
      "This report examines India's agriculture and food-processing value chain, from primary production and cold-chain infrastructure to export readiness and global market access.",
      "It identifies post-harvest losses, fragmented supply chains and inconsistent quality standards as the principal constraints, and proposes a cluster-based mega-food-park model to address them.",
      "The study also evaluates the impact of digital marketplaces, drone technology and precision agriculture on farmer incomes.",
    ],
    highlights: [
      "Cluster-based mega-food-park model",
      "Post-harvest loss reduction strategy",
      "Drone & precision-agri adoption index",
    ],
  },
  {
    id: "report-6",
    title: "Skill Gap Analysis in Industry 4.0",
    subtitle: "Preparing the Future Workforce",
    category: "Skill & Education",
    date: "October 2024",
    pages: 67,
    size: "3.5 MB",
    format: "PDF",
    author: "ASSOCHAM Skill Council",
    accent: "from-brand via-ember to-sun",
    icon: "graduation",
    overview: [
      "The Skill Gap Analysis in Industry 4.0 quantifies the gap between the skills India's workforce possesses and those its industries will demand as automation, AI and advanced manufacturing reshape the factory floor.",
      "The report projects a net incremental demand of 120 million skilled workers by 2030 and identifies the most acute shortages in robotics, data analytics, cybersecurity and advanced maintenance.",
      "It recommends a dual-track model combining short-term industry-led certifications with long-term apprenticeship embedded in higher education.",
    ],
    highlights: [
      "120 million skilled-worker demand by 2030",
      "4 critical skill-shortage domains",
      "Dual-track apprenticeship model",
    ],
  },
];

export const voices = [
  {
    id: "voice-1",
    title: "Union Budget 2025-26: Industry Expectations",
    subtitle: "A Pre-Budget Advocacy Memorandum",
    category: "Fiscal Policy",
    date: "March 2025",
    pages: 28,
    size: "1.8 MB",
    format: "PDF",
    author: "ASSOCHAM Economic Policy Desk",
    accent: "from-brand via-crimson to-deeprose",
    icon: "rupee",
    overview: [
      "ASSOCHAM's pre-budget memorandum consolidates the recommendations of over 2,500 member companies across 18 sectors, presenting a unified industry voice ahead of the Union Budget 2025-26.",
      "The document calls for a stable, predictable direct-tax regime, a simplified GST compliance framework and a stepped-up capital expenditure programme focused on logistics and energy infrastructure.",
      "It also urges targeted incentives for research and development, green manufacturing and the formalisation of MSMEs.",
    ],
    highlights: [
      "2,500+ member company recommendations",
      "18-sector consolidated advocacy matrix",
      "GST simplification & R&D incentive roadmap",
    ],
  },
  {
    id: "voice-2",
    title: "Ease of Doing Business: A New Roadmap",
    subtitle: "Decriminalisation & Digitisation",
    category: "Regulatory Reform",
    date: "February 2025",
    pages: 34,
    size: "2.1 MB",
    format: "PDF",
    author: "ASSOCHAM Regulatory Reform Council",
    accent: "from-ember via-sun to-brand",
    icon: "gavel",
    overview: [
      "This advocacy paper proposes a new ease-of-doing-business roadmap built on two pillars: the decriminalisation of minor commercial offences and the end-to-end digitisation of approvals and inspections.",
      "It recommends a shift from arrest-and-prosecution to compounding-and-penalty for a defined list of procedural violations, and a single-window system integrating 42 central and state approvals.",
      "The paper benchmarks India's reform trajectory against Singapore, the UAE and Vietnam, identifying the next 10 high-impact measures.",
    ],
    highlights: [
      "Decriminalisation of 42 procedural offences",
      "Single-window system for 42 approvals",
      "10 next-gen reform measures benchmarked",
    ],
  },
  {
    id: "voice-3",
    title: "Climate Action & Industry Responsibility",
    subtitle: "Equitable Pathways to Net-Zero",
    category: "Climate & ESG",
    date: "January 2025",
    pages: 40,
    size: "2.4 MB",
    format: "PDF",
    author: "ASSOCHAM Climate Council",
    accent: "from-deeprose via-brand to-ember",
    icon: "globe",
    overview: [
      "ASSOCHAM's position paper on climate action articulates the industry's commitment to net-zero while emphasising the principle of common but differentiated responsibilities and the need for climate finance.",
      "It calls for a predictable carbon-market architecture, accelerated deployment of renewables and a just-transition framework that protects workers and communities.",
      "The paper also proposes a voluntary ESG disclosure standard aligned with global frameworks but calibrated to Indian industry realities.",
    ],
    highlights: [
      "Predictable carbon-market architecture",
      "Just-transition protection framework",
      "India-aligned voluntary ESG standard",
    ],
  },
  {
    id: "voice-4",
    title: "India's PLI Schemes: An Assessment",
    subtitle: "Outcomes, Gaps & Next Steps",
    category: "Industrial Policy",
    date: "December 2024",
    pages: 31,
    size: "1.9 MB",
    format: "PDF",
    author: "ASSOCHAM Manufacturing Council",
    accent: "from-sun via-ember to-crimson",
    icon: "gear",
    overview: [
      "This assessment reviews the performance of the Production-Linked Incentive schemes across 14 sectors, evaluating disbursement, capacity addition, employment generation and import substitution.",
      "It finds that while the schemes have catalysed significant fresh investment, their full potential is constrained by delayed disbursements, rigid eligibility criteria and limited MSME participation.",
      "The paper recommends a faster payout cycle, a graded incentive structure and a dedicated MSME window within each scheme.",
    ],
    highlights: [
      "14-sector PLI outcome review",
      "Faster payout & graded incentive proposals",
      "Dedicated MSME window recommended",
    ],
  },
  {
    id: "voice-5",
    title: "Skilling India for Global Markets",
    subtitle: "From Talent Pool to Talent Magnet",
    category: "Skill & Employment",
    date: "November 2024",
    pages: 26,
    size: "1.6 MB",
    format: "PDF",
    author: "ASSOCHAM Skill Council",
    accent: "from-ember via-brand to-deeprose",
    icon: "users",
    overview: [
      "This advocacy note positions India as a global talent magnet, calling for a skill architecture that is demand-led, industry-recognised and globally portable.",
      "It proposes a credit-framework linking short-term certifications to formal degrees, a bilateral mutual-recognition agreement drive with key partner economies, and a national apprenticeship expansion programme.",
      "The note also addresses the gender dimension of skilling, with targets for women's participation in non-traditional sectors.",
    ],
    highlights: [
      "Credit-framework linking certifications to degrees",
      "Bilateral skill mutual-recognition drive",
      "Women-in-non-traditional-sector targets",
    ],
  },
  {
    id: "voice-6",
    title: "Digital Public Infrastructure for Business",
    subtitle: "Leveraging India Stack",
    category: "Digital Economy",
    date: "October 2024",
    pages: 29,
    size: "1.8 MB",
    format: "PDF",
    author: "ASSOCHAM Digital Council",
    accent: "from-brand via-ember to-sun",
    icon: "layers",
    overview: [
      "This paper explores how India's digital public infrastructure, including Aadhaar, UPI, ONDC and the account aggregator framework, can be leveraged to reduce business friction and unlock new markets.",
      "It recommends the expansion of ONDC to B2B trade, the integration of the account aggregator framework into MSME lending, and the creation of a trusted data-exchange layer for industry.",
      "The paper also addresses governance, privacy and cybersecurity considerations for a scalable digital business ecosystem.",
    ],
    highlights: [
      "ONDC expansion to B2B trade",
      "Account aggregator integration for MSME lending",
      "Trusted data-exchange governance model",
    ],
  },
];

export const bulletins = [
  {
    id: "bulletin-1",
    title: "Monthly Economic Review - March 2025",
    subtitle: "Growth, Inflation & Fiscal Indicators",
    category: "Monthly Bulletin",
    date: "March 2025",
    pages: 22,
    size: "1.4 MB",
    format: "PDF",
    author: "ASSOCHAM Economics Desk",
    accent: "from-brand via-crimson to-deeprose",
    icon: "calendar",
    overview: [
      "The Monthly Economic Review for March 2025 tracks the latest high-frequency indicators across growth, inflation, fiscal deficit, trade and credit offtake.",
      "This edition notes a firming of industrial output, a moderation in core inflation and a steady improvement in government capex, while flagging risks from global commodity prices and capital-flow volatility.",
      "The bulletin includes a sector spotlight on construction and real estate, and a forward-looking assessment of the monsoon outlook.",
    ],
    highlights: [
      "High-frequency indicator dashboard",
      "Construction & real-estate sector spotlight",
      "Forward-looking monsoon assessment",
    ],
  },
  {
    id: "bulletin-2",
    title: "Trade & Tariff Bulletin - Q1 2025",
    subtitle: "Global Shifts & India's Position",
    category: "Quarterly Bulletin",
    date: "April 2025",
    pages: 30,
    size: "1.9 MB",
    format: "PDF",
    author: "ASSOCHAM Trade Council",
    accent: "from-ember via-sun to-brand",
    icon: "ship",
    overview: [
      "The Trade & Tariff Bulletin for Q1 2025 analyses the evolving global trade landscape, including supply-chain realignments, new bilateral agreements and tariff movements across key markets.",
      "This edition examines India's emerging trade partnerships, the impact of Red Sea disruptions on logistics costs, and the opportunities opening up in green goods and services exports.",
      "It includes a tariff tracker covering 15 key export sectors and a market-access matrix for 20 partner economies.",
    ],
    highlights: [
      "15-sector tariff tracker",
      "20-economy market-access matrix",
      "Red Sea logistics-cost impact analysis",
    ],
  },
  {
    id: "bulletin-3",
    title: "Regulatory Compass - February 2025",
    subtitle: "Notifications, Circulars & Amendments",
    category: "Monthly Bulletin",
    date: "February 2025",
    pages: 18,
    size: "1.1 MB",
    format: "PDF",
    author: "ASSOCHAM Regulatory Desk",
    accent: "from-deeprose via-brand to-ember",
    icon: "compass",
    overview: [
      "The Regulatory Compass curates and decodes the month's key regulatory notifications, circulars and amendments across central and state jurisdictions.",
      "This edition covers changes in labour codes, environmental clearances, data-protection rules and food-safety standards, with plain-language summaries and compliance timelines.",
      "A dedicated Q&A segment addresses member-raised queries on recent regulatory developments.",
    ],
    highlights: [
      "Labour, environment & data-protection updates",
      "Plain-language compliance summaries",
      "Member Q&A segment included",
    ],
  },
  {
    id: "bulletin-4",
    title: "Sector Watch: Auto & EV",
    subtitle: "Mobility Transformation Tracker",
    category: "Sector Bulletin",
    date: "January 2025",
    pages: 24,
    size: "1.5 MB",
    format: "PDF",
    author: "ASSOCHAM Auto Council",
    accent: "from-sun via-ember to-crimson",
    icon: "car",
    overview: [
      "Sector Watch: Auto & EV tracks the transformation of India's mobility landscape, from electric-vehicle adoption and battery manufacturing to connected-car technology and policy incentives.",
      "This edition analyses the impact of the FAME-II successor scheme, the entry of new EV original equipment manufacturers, and the evolving charging-infrastructure rollout.",
      "It includes a sales-trend dashboard, a policy-calendar tracker and an interview with an industry leader on the road to 30% EV penetration.",
    ],
    highlights: [
      "EV sales & charging-infrastructure dashboard",
      "FAME-II successor scheme analysis",
      "Industry-leader interview feature",
    ],
  },
  {
    id: "bulletin-5",
    title: "Global Trade Digest",
    subtitle: "World Markets at a Glance",
    category: "Monthly Bulletin",
    date: "December 2024",
    pages: 20,
    size: "1.3 MB",
    format: "PDF",
    author: "ASSOCHAM International Trade Desk",
    accent: "from-ember via-brand to-deeprose",
    icon: "globe",
    overview: [
      "The Global Trade Digest provides a curated round-up of the month's most significant international trade developments, from WTO rulings and regional agreements to commodity-market shifts.",
      "This edition covers the Indo-Pacific Economic Framework negotiations, the EU's Carbon Border Adjustment Mechanism, and the evolving India-GCC trade corridor.",
      "It includes a market-opportunity scan for Indian exporters across 12 high-potential destinations.",
    ],
    highlights: [
      "IPEF & CBAM compliance analysis",
      "India-GCC trade-corridor update",
      "12-destination export opportunity scan",
    ],
  },
  {
    id: "bulletin-6",
    title: "Policy Pulse Weekly",
    subtitle: "Decisions, Announcements & Signals",
    category: "Weekly Bulletin",
    date: "Week of 18 Nov 2024",
    pages: 12,
    size: "0.8 MB",
    format: "PDF",
    author: "ASSOCHAM Policy Watch",
    accent: "from-brand via-ember to-sun",
    icon: "pulse",
    overview: [
      "Policy Pulse Weekly distils the week's key policy announcements, cabinet decisions and regulatory signals into a concise, action-oriented brief.",
      "This issue covers the cabinet approval of a new logistics policy, RBI's latest monetary stance, and the notification of revised quality control orders for select consumer goods.",
      "Each item is presented with a one-line impact assessment and a recommended member action, making the bulletin immediately usable.",
    ],
    highlights: [
      "Weekly cabinet & RBI signal tracker",
      "One-line impact assessments",
      "Recommended member actions per item",
    ],
  },
];

/* About Knowledge — intro copy + contact used on the About page. */
export const aboutKnowledge = {
  icon: "/images/about-knowledge.jpg",
  heading: "About Knowledge",
  paragraphs: [
    "ASSOCHAM is focused on building a strong network of knowledge architects with deep expertise across industries and regions. The Chamber works closely with Government and industry stakeholders on critical challenges, resulting in innovative perspectives and insights. ASSOCHAM publications focus on providing insights to help better understand the global and regional trends and opportunities that will shape various sectors and define the India growth opportunity.",
    "The ASSOCHAM knowledge base is built on decades of experience supporting a wide range of industries, demonstrating our understanding of complex business challenges faced by the Indian industry and contribute to innovative ideas that can facilitate accelerated growth. We regularly share industry-focused opinions from experts on the latest trends, topics and issues affecting the economy.",
  ],
  contact: {
    name: "Manju R S Negi",
    email: "research@assocham.com",
  },
};

/* Featured Reports — carousel items shown on the About page.
   `id` links back to a full report in `reports` for the detail view. */
export const featuredReports = [
  {
    id: "report-1",
    title: "India's Entrepreneurial Journey",
    date: "Jun, 2022",
    cover: "from-sky-100 via-cyan-50 to-white",
    coverText: "India's Entrepreneurial Edge",
    coverSub: "Strengthening the Economic Growth Journey",
    coverTheme: "light",
    icon: "spark",
  },
  {
    id: "report-3",
    title: "The Great Reset: Strengthening India's Competitiveness",
    date: "Mar, 2022",
    cover: "from-slate-900 via-ink to-deeprose",
    coverText: "The Great Reset",
    coverSub: "Strengthening India's Competitiveness",
    coverTheme: "dark",
    icon: "globe",
  },
  {
    id: "report-2",
    title: "MSME Competitiveness Index",
    date: "Feb, 2025",
    cover: "from-ember via-sun to-brand",
    coverText: "MSME Index",
    coverSub: "Unlocking the Backbone of the Economy",
    coverTheme: "dark",
    icon: "chart",
  },
  {
    id: "report-4",
    title: "Digital Economy Readiness Study",
    date: "Dec, 2024",
    cover: "from-brand via-crimson to-deeprose",
    coverText: "Digital Economy",
    coverSub: "India's Stack, Scale & Strategy",
    coverTheme: "dark",
    icon: "chip",
  },
  {
    id: "report-5",
    title: "Agriculture & Food Processing Report",
    date: "Nov, 2024",
    cover: "from-lime-100 via-emerald-50 to-white",
    coverText: "Farm to Market",
    coverSub: "Agriculture & Food Processing",
    coverTheme: "light",
    icon: "wheat",
  },
  {
    id: "report-6",
    title: "Skill Gap Analysis in Industry 4.0",
    date: "Oct, 2024",
    cover: "from-deeprose via-brand to-ember",
    coverText: "Skill Gap 4.0",
    coverSub: "Preparing the Future Workforce",
    coverTheme: "dark",
    icon: "graduation",
  },
];

/* Convenience map used by routing + detail view */
export const knowledgeMap = {
  reports,
  voices,
  bulletins,
};

export const sectionMeta = {
  reports: {
    key: "reports",
    label: "ASSOCHAM Reports",
    short: "Reports",
    tagline: "Research-led insights for industry leadership",
    description:
      "In-depth research reports produced by ASSOCHAM's specialist councils, benchmarking India's competitiveness and charting sector roadmaps.",
    hero: "/images/report-hero.jpg",
    accent: "from-brand via-crimson to-deeprose",
  },
  voices: {
    key: "voices",
    label: "ASSOCHAM Voice",
    short: "Voice",
    tagline: "The unified voice of Indian industry",
    description:
      "Advocacy memoranda, position papers and policy recommendations distilled from the collective wisdom of ASSOCHAM's 2,500+ member companies.",
    hero: "/images/voice-hero.jpg",
    accent: "from-ember via-sun to-brand",
  },
  bulletins: {
    key: "bulletins",
    label: "ASSOCHAM Bulletins",
    short: "Bulletins",
    tagline: "The economy, decoded every week",
    description:
      "Periodical bulletins tracking the economy, trade, regulation and sectors — designed to keep members ahead of the curve.",
    hero: "/images/bulletin-hero.jpg",
    accent: "from-deeprose via-brand to-ember",
  },
};
